import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useVersion } from "../context/VersionContext";
import { LayoutDashboard, FileText, Plus, LogOut, FileSignature, Building2, Users, ShieldAlert, Package, KeyRound } from "lucide-react";
import { Button } from "./ui/button";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/quotes", label: "Preventivi", icon: FileText, id: "nav-quotes" },
  { to: "/quotes/new", label: "Nuovo Preventivo", icon: Plus, id: "nav-new-quote" },
  { to: "/catalog", label: "Catalogo Prodotti", icon: Package, id: "nav-catalog" },
  { to: "/settings", label: "La mia azienda", icon: Building2, id: "nav-settings" },
];

const adminNav = [
  { to: "/admin", label: "Gestione utenti", icon: Users, id: "nav-admin" },
  { to: "/security", label: "Sicurezza", icon: ShieldAlert, id: "nav-security" },
  { to: "/setup", label: "Setup", icon: KeyRound, id: "nav-setup" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { version } = useVersion();
  const location = useLocation();
  const navigate = useNavigate();
  const items = user?.role === "admin" ? [...nav, ...adminNav] : nav;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex">
      <aside className="w-64 bg-slate-50 border-r border-slate-200 hidden md:flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-slate-200 flex items-center gap-2">
          <FileSignature className="w-6 h-6 text-blue-600" strokeWidth={2} />
          <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900">
            Preventivatore
          </span>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {items.map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  active
                    ? "bg-blue-600 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-slate-200">
          <div className="px-3 mb-2">
            <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full justify-start text-slate-600 hover:text-red-600"
          >
            <LogOut className="w-4 h-4 mr-2" /> Esci
          </Button>
          <div
            data-testid="app-version-badge"
            className="mt-3 px-3 flex items-center gap-1.5 text-[11px] font-medium text-slate-400"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
            V.{version || "…"}
          </div>
        </div>
      </aside>

      <div className="flex-1 md:ml-64">
        <header className="md:hidden backdrop-blur-xl bg-white/80 border-b border-slate-200 sticky top-0 z-40 px-4 py-3 flex items-center justify-between">
          <span className="font-heading font-extrabold text-slate-900">Preventivatore</span>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button-mobile">
            <LogOut className="w-4 h-4" />
          </Button>
        </header>
        <main className="p-6 md:p-8 lg:p-10 max-w-6xl mx-auto">{children}</main>
      </div>
    </div>
  );
}
